(() => {
  'use strict';

  const VERSION = '3.11.1';
  if (window.MoztechBurnReveal?.version === VERSION) return;

  const VERTEX_SHADER = `
    attribute vec2 aPosition;
    varying vec2 vUv;

    void main() {
      vUv = aPosition * 0.5 + 0.5;
      gl_Position = vec4(aPosition, 0.0, 1.0);
    }
  `;

  const FRAGMENT_SHADER = `
    precision mediump float;

    varying vec2 vUv;
    uniform sampler2D uMask;
    uniform vec2 uResolution;
    uniform vec2 uMaskTexel;
    uniform vec2 uMaskGrid;
    uniform vec2 uMaskTextureSize;
    uniform vec3 uGlowColor;
    uniform float uMaskReady;
    uniform float uMaskFrameA;
    uniform float uMaskFrameB;
    uniform float uMaskFrameMix;
    uniform float uProgress;
    uniform float uTime;

    vec2 atlasUv(vec2 uv, float frameIndex) {
      vec2 safeUv = clamp(uv, vec2(0.0), vec2(1.0));
      float column = mod(frameIndex, uMaskGrid.x);
      // The atlas is authored top-to-bottom, while WebGL uploads it with Y flipped
      // to preserve the original video-mask orientation inside every frame.
      float row = (uMaskGrid.y - 1.0) - floor(frameIndex / uMaskGrid.x);
      vec2 cell = vec2(column, row);
      vec2 atlasMin = cell / uMaskGrid;
      vec2 atlasMax = (cell + 1.0) / uMaskGrid;
      vec2 halfTexel = vec2(0.5) / max(uMaskTextureSize, vec2(1.0));
      return clamp((cell + safeUv) / uMaskGrid, atlasMin + halfTexel, atlasMax - halfTexel);
    }

    float sampleMask(vec2 uv) {
      float frameA = texture2D(uMask, atlasUv(uv, uMaskFrameA)).r;
      float frameB = texture2D(uMask, atlasUv(uv, uMaskFrameB)).r;
      return mix(frameA, frameB, uMaskFrameMix);
    }

    float hash21(vec2 point) {
      point = fract(point * vec2(123.34, 456.21));
      point += dot(point, point + 45.32);
      return fract(point.x * point.y);
    }

    float noise21(vec2 point) {
      vec2 cell = floor(point);
      vec2 local = fract(point);
      local = local * local * (3.0 - 2.0 * local);
      float a = hash21(cell);
      float b = hash21(cell + vec2(1.0, 0.0));
      float c = hash21(cell + vec2(0.0, 1.0));
      float d = hash21(cell + vec2(1.0, 1.0));
      return mix(mix(a, b, local.x), mix(c, d, local.x), local.y);
    }

    float fallbackMask(vec2 uv) {
      vec2 point = uv - vec2(0.5, 0.48);
      float viewportAspect = uResolution.x / max(uResolution.y, 1.0);
      point.x *= viewportAspect;
      float angle = atan(point.y, point.x);
      float turbulence =
        sin(angle * 5.0 - 0.8) * 0.018 +
        sin(angle * 11.0 + 1.7) * 0.011 +
        sin(angle * 23.0 - 2.2) * 0.006;
      turbulence += sin(point.x * 19.0 + point.y * 13.0) * 0.008;
      float mobileWeight = 1.0 - step(0.85, viewportAspect);
      float timeline = pow(clamp(uProgress, 0.0, 1.0), mix(1.95, 2.15, mobileWeight));
      float eased = 1.0 - pow(1.0 - clamp(timeline, 0.0, 1.0), 2.35);
      float viewportRadius = length(vec2(viewportAspect * 0.5, 0.52));
      float radius = mix(-0.05, viewportRadius * 1.13, eased);
      return smoothstep(-0.105, 0.105, radius - length(point) - turbulence);
    }

    void main() {
      vec2 uv = vUv;
      float viewportAspect = uResolution.x / max(uResolution.y, 1.0);
      const float maskAspect = 1.7777778;
      vec2 maskUv = uv - 0.5;
      if (viewportAspect > maskAspect) {
        maskUv.y *= maskAspect / viewportAspect;
      } else {
        maskUv.x *= viewportAspect / maskAspect;
      }
      maskUv += 0.5;

      float videoMask = sampleMask(maskUv);
      float proceduralMask = fallbackMask(uv);
      // A 16:9 Luma source must be cropped heavily on portrait screens. Blend
      // an aspect-correct procedural contour into that crop so the white burn
      // edge stays inside every phone viewport while retaining atlas texture.
      float portraitAtlasWeight = mix(0.50, 1.0, smoothstep(0.62, 1.0, viewportAspect));
      float videoWeight = uMaskReady * portraitAtlasWeight;
      float rawMask = mix(proceduralMask, videoMask, videoWeight);

      vec2 stage = uv - vec2(0.5, 0.48);
      stage.x *= uResolution.x / max(uResolution.y, 1.0) * 0.58;
      float radial = length(stage);
      float angle = atan(stage.y, stage.x);
      // Keep the energetic edge close to the actual luma boundary. The old
      // broad zone washed the outside of the frame white and read as an
      // outside-in flash instead of a center ignition.
      float boundaryZone = 1.0 - smoothstep(0.075, 0.265, abs(rawMask - 0.5));
      float broadGrain = noise21(uv * vec2(63.0, 37.0) + vec2(uTime * 0.34, -uTime * 0.21));
      float fineGrain = hash21(floor(uv * uResolution * 0.72));
      float fiberWave = sin(angle * 118.0 + radial * 176.0 + broadGrain * 8.0 - uTime * 3.2) * 0.5 + 0.5;
      float breakupStrength = mix(0.28, 1.0, videoWeight);
      float breakup = ((broadGrain - 0.5) * 0.58 + (fiberWave - 0.5) * 0.34) * boundaryZone * breakupStrength;
      float maskValue = smoothstep(0.42, 0.58, rawMask + breakup);

      vec2 nearOffset = uMaskTexel * 2.5;
      vec2 farOffset = uMaskTexel * 7.0;
      float nearMax = max(
        max(sampleMask(maskUv + vec2(nearOffset.x, 0.0)), sampleMask(maskUv - vec2(nearOffset.x, 0.0))),
        max(sampleMask(maskUv + vec2(0.0, nearOffset.y)), sampleMask(maskUv - vec2(0.0, nearOffset.y)))
      );
      float nearMin = min(
        min(sampleMask(maskUv + vec2(nearOffset.x, 0.0)), sampleMask(maskUv - vec2(nearOffset.x, 0.0))),
        min(sampleMask(maskUv + vec2(0.0, nearOffset.y)), sampleMask(maskUv - vec2(0.0, nearOffset.y)))
      );
      float farMax = max(
        max(sampleMask(maskUv + vec2(farOffset.x, 0.0)), sampleMask(maskUv - vec2(farOffset.x, 0.0))),
        max(sampleMask(maskUv + vec2(0.0, farOffset.y)), sampleMask(maskUv - vec2(0.0, farOffset.y)))
      );
      nearMax = mix(rawMask, nearMax, videoWeight);
      nearMin = mix(rawMask, nearMin, videoWeight);
      farMax = mix(rawMask, farMax, videoWeight);

      float whiteCore = 1.0 - smoothstep(0.022, 0.112, abs(rawMask - 0.5));
      float coreBreakup = smoothstep(0.26, 0.78, broadGrain * 0.68 + fiberWave * 0.32);
      float hotBand = whiteCore * (0.38 + coreBreakup * 0.88) * (0.82 + fiberWave * 0.24);
      float contour = clamp((nearMax - nearMin) * 2.8, 0.0, 1.0);
      float halo = clamp(boundaryZone * 0.55 + (farMax - rawMask) * 1.15 + contour * 0.42, 0.0, 1.0);
      float edgeFlicker = 0.94 + 0.06 * sin(uTime * 13.0 + uv.x * 83.0 - uv.y * 61.0 + broadGrain * 9.0);

      float dust = smoothstep(0.70, 0.995, fineGrain) * boundaryZone;
      float fiberDust = smoothstep(0.86, 0.99, fiberWave) * smoothstep(0.34, 0.92, broadGrain) * boundaryZone;

      float insideSphere = smoothstep(0.50, 0.72, rawMask);
      // Shopify's reference transition first builds a solid white ignition in
      // the center, then burns a hole through that white surface to reveal the
      // next scene. This is deliberately separate from the hot edge so the
      // effect never collapses into a single expanding outline.
      float ignitionEnvelope =
        smoothstep(0.012, 0.085, uProgress) *
        (1.0 - smoothstep(0.24, 0.47, uProgress));
      float ignitionTexture = smoothstep(0.28, 0.80, broadGrain * 0.62 + fiberWave * 0.38);
      float ignitionFill =
        smoothstep(0.34, 0.66, rawMask + (broadGrain - 0.5) * 0.055) *
        ignitionEnvelope *
        (0.88 + ignitionTexture * 0.18);
      float veilEnvelope =
        smoothstep(0.035, 0.16, uProgress) *
        (1.0 - smoothstep(0.58, 0.84, uProgress));
      float shellNoise = noise21(uv * vec2(19.0, 13.0) + vec2(-uTime * 0.26, uTime * 0.19));
      float shellWisps = smoothstep(0.42, 0.84, shellNoise) * insideSphere * veilEnvelope;
      float shellGrain = hash21(floor(uv * uResolution * 0.92) + 71.0);
      float shellCluster = smoothstep(0.43, 0.88, shellNoise);
      float shellDust = smoothstep(0.91, 0.999, shellGrain) * shellCluster * insideSphere * veilEnvelope;
      float shellFibers =
        smoothstep(0.968, 0.998, sin(angle * 86.0 - radial * 138.0 + shellNoise * 11.0) * 0.5 + 0.5) *
        smoothstep(0.66, 0.96, shellNoise) *
        step(0.58, shellGrain) *
        insideSphere * veilEnvelope;

      vec2 starGrid = vec2(50.0, 28.0);
      vec2 starCell = floor(uv * starGrid);
      vec2 starLocal = fract(uv * starGrid) - 0.5;
      float starSeed = hash21(starCell + 17.0);
      float horizontalRay = exp(-abs(starLocal.x) * 34.0) * exp(-abs(starLocal.y) * 7.0);
      float verticalRay = exp(-abs(starLocal.y) * 34.0) * exp(-abs(starLocal.x) * 7.0);
      float starPulse = 0.72 + 0.28 * sin(uTime * (4.0 + starSeed * 3.0) + starSeed * 47.0);
      float edgeStars = step(0.974, starSeed) * (horizontalRay + verticalRay) * boundaryZone * starPulse;
      float sphereStars = step(0.982, starSeed) * (horizontalRay + verticalRay) * insideSphere * veilEnvelope * starPulse;
      float stars = max(edgeStars, sphereStars);

      float curtainAlpha = 1.0 - smoothstep(0.46, 0.58, maskValue);
      float particulate = clamp(dust * 0.95 + fiberDust * 0.82, 0.0, 1.0);
      float shellLight = clamp(shellDust + shellFibers * 0.66, 0.0, 1.0);
      float shellShadow = shellWisps * (0.04 + (1.0 - shellGrain) * 0.12);
      float glowAlpha = clamp(hotBand + halo * 0.42 + particulate * 0.82 + shellLight * 0.72 + stars, 0.0, 1.0);
      float alpha = max(max(max(curtainAlpha, glowAlpha), ignitionFill), shellShadow);
      vec3 hotColor = mix(uGlowColor, vec3(1.0), 0.72);
      vec3 glow = hotColor * (
        hotBand * 1.72 * edgeFlicker +
        halo * 0.42 +
        particulate * (0.90 + edgeFlicker * 0.20) +
        shellLight * 0.78 +
        stars * 2.8
      );
      vec3 ignition = mix(uGlowColor, vec3(1.0), 0.90) * ignitionFill * 1.10;

      // Finish transparently inside the shader instead of hiding the canvas in
      // CSS. Keeping the WebGL surface composited prevents Safari and mobile
      // GPUs from discarding the framebuffer before a reverse scroll.
      float completionFade = 1.0 - smoothstep(0.965, 0.999, uProgress);
      gl_FragColor = vec4((glow + ignition) * completionFade, alpha * completionFade);
    }
  `;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / Math.max(end - start, 0.0001), 0, 1);
    return progress * progress * (3 - 2 * progress);
  };

  const parseHexColor = (hex) => {
    const normalized = String(hex || '#f8fcff').replace('#', '').trim();
    const value = normalized.length === 3
      ? normalized.split('').map((character) => character + character).join('')
      : normalized.padEnd(6, 'f').slice(0, 6);
    return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16) / 255);
  };

  const waitForEvent = (target, eventName, timeout = 1800) => new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      target.removeEventListener(eventName, onReady);
      target.removeEventListener('error', onError);
      resolve(result);
    };
    const onReady = () => finish(true);
    const onError = () => finish(false);
    const timer = window.setTimeout(() => finish(false), timeout);
    target.addEventListener(eventName, onReady, { once: true });
    target.addEventListener('error', onError, { once: true });
  });

  class MoztechBurnRevealInstance {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-moztech-burn-canvas]');
      this.heroVideo = root.querySelector('video.moztech-hero-bg');
      this.fragments = [...root.querySelectorAll('[data-moztech-fusion-fragment]')];
      this.motionMode = root.dataset.burnMotion === 'once' ? 'once' : 'scroll';
      this.duration = clamp(Number(root.dataset.burnDuration) || 2800, 1200, 3600);
      this.editorDuration = clamp(Math.max(this.duration * 2.8, 7600), 7600, 9200);
      this.activeDuration = this.duration;
      this.maskDuration = 2.8;
      this.maskAtlasUrl = root.dataset.burnMaskAtlasUrl;
      this.maskAtlasColumns = 8;
      this.maskAtlasRows = 6;
      this.maskFrameCount = 48;
      this.maskFrameWidth = 256;
      this.maskFrameHeight = 144;
      this.glowColor = parseHexColor(root.dataset.burnColor);
      this.fragmentIntensity = clamp(Number(root.dataset.burnFragmentIntensity) || 0.75, 0.4, 1);
      this.fragmentSizeScale = clamp(Number(root.dataset.burnFragmentSizeScale) || 1.3, 0.9, 1.8);
      this.pacing = ['compact', 'cinematic'].includes(root.dataset.burnPacing)
        ? root.dataset.burnPacing
        : 'standard';
      this.hasCustomFragments = this.fragments.some((fragment) => fragment.classList.contains('is-custom'));
      this.storyHandoff = this.findStoryHandoff();
      this.hasStoryHandoff = Boolean(this.storyHandoff);
      this.sectionId = root.dataset.sectionId;
      this.isDesignMode = Boolean(
        window.Shopify?.designMode
        || document.documentElement.classList.contains('shopify-design-mode')
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const debugMode = new URLSearchParams(window.location.search).get('debug');
      this.forceCssFallback = debugMode === 'burn-css-fallback';
      this.forceProcedural = debugMode === 'burn-procedural';
      this.destroyed = false;
      this.heroPlayPromise = null;
      this.heroPlaybackRequested = false;
      this.running = false;
      this.completed = false;
      this.preparing = null;
      this.maskReady = false;
      this.maskUploaded = false;
      this.maskImage = null;
      this.currentProgress = -1;
      this.desiredProgress = 0;
      this.burnProgress = 0;
      this.lastScrollY = window.scrollY;
      this.animationFrame = 0;
      this.scrollFrame = 0;
      this.editorPreviewTimer = 0;
      this.prepareObserver = null;
      this.startObserver = null;
      this.resizeObserver = null;
      this.needsResize = true;
      this.presentationProgress = -1;
      this.handoffFragmentsHidden = false;
      this.onScroll = () => this.queueScrollUpdate();
      this.onResize = () => this.queueScrollUpdate(true);
      this.onContextLost = (event) => {
        event.preventDefault();
        this.gl = null;
        this.program = null;
        this.maskTexture = null;
        this.positionBuffer = null;
        this.maskUploaded = false;
        this.enableCssFallback('webgl-context-lost');
        this.applyPresentation(Math.max(this.currentProgress, 0));
      };
      this.onContextRestored = () => {
        if (this.destroyed || !this.initWebGL()) return;
        this.uploadMaskAtlas();
        this.resize();
        if (this.motionMode === 'scroll') this.drawScrollProgress(Math.max(this.currentProgress, 0));
      };

      if (!this.canvas) return;

      this.canvas.addEventListener('webglcontextlost', this.onContextLost, false);
      this.canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);
      this.root.dataset.burnState = 'idle';
      this.root.dataset.burnTimeline = 'inward-gather-center-burn-reversible-atlas';
      this.root.dataset.burnHandoff = this.hasStoryHandoff ? 'story' : 'standalone';
      this.root.dataset.burnEditorDuration = String(this.editorDuration);
      this.bindObservers();
    }

    findStoryHandoff() {
      const wrapper = this.root.closest('.shopify-section') || this.root.parentElement;
      let sibling = wrapper?.previousElementSibling || null;
      let checks = 0;
      while (sibling && checks < 3) {
        if (sibling.matches?.('[data-moztech-illuminated-story]') || sibling.querySelector?.('[data-moztech-illuminated-story]')) {
          return sibling.matches?.('[data-moztech-illuminated-story]')
            ? sibling
            : sibling.querySelector?.('[data-moztech-illuminated-story]');
        }
        sibling = sibling.previousElementSibling;
        checks += 1;
      }
      return null;
    }

    bindObservers() {
      if (this.reducedMotion) {
        this.applyPresentation(1);
        this.root.classList.add('is-burn-complete');
        this.root.dataset.burnState = 'reduced-motion';
        return;
      }

      if (this.isDesignMode && this.motionMode === 'scroll') {
        this.showEditorPreview();
        return;
      }

      if ('IntersectionObserver' in window) {
        const prepareTarget = this.storyHandoff || this.root;
        const prepareRootMargin = this.hasStoryHandoff ? '35% 0px 35% 0px' : '80% 0px 80% 0px';
        this.root.dataset.mediaPrepareTarget = this.hasStoryHandoff ? 'story-handoff' : 'hero';
        this.prepareObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.prepare();
          this.prepareObserver?.disconnect();
        }, { rootMargin: prepareRootMargin, threshold: 0 });

        this.prepareObserver.observe(prepareTarget);

        if (this.motionMode === 'once') {
          this.startObserver = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.22)) return;
            this.start();
            this.startObserver?.disconnect();
          }, { rootMargin: '0px 0px -10% 0px', threshold: [0.22, 0.45] });
          this.startObserver.observe(this.root);
        }
      } else {
        this.prepare();
        if (this.motionMode === 'once') this.start();
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.needsResize = true;
          if (this.motionMode === 'scroll') this.queueScrollUpdate(true);
          else this.resize();
        });
        this.resizeObserver.observe(this.root);
      }

      if (this.motionMode === 'scroll') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.queueScrollUpdate(true);
      }

      if (this.isDesignMode) this.prepare();
    }

    showEditorPreview() {
      // Theme Editor scrolls an outer canvas, so it cannot scrub this section's
      // internal scroll progress. Unselected sections hold on the most legible
      // burn frame; Shopify section selection explicitly starts the preview.
      const previewProgress = 0.55;
      this.applyPresentation(previewProgress);
      this.desiredProgress = previewProgress;
      this.currentProgress = previewProgress;
      this.root.classList.remove('is-burn-complete', 'is-burn-reversing');
      this.root.classList.add('is-burn-active');
      this.root.dataset.burnState = 'editor-preview-burn-hold';
      this.root.dataset.burnDirection = 'hold';
      this.root.dataset.burnProgress = previewProgress.toFixed(3);
      this.heroVideo?.pause();
      this.prepare().then(() => {
        if (this.destroyed || !this.isDesignMode) return;
        this.drawScrollProgress(this.desiredProgress);
      });
    }

    async previewEditorBurn() {
      if (!this.isDesignMode || this.motionMode !== 'scroll' || this.destroyed || this.reducedMotion) return;

      window.clearTimeout(this.editorPreviewTimer);
      cancelAnimationFrame(this.animationFrame);
      this.running = true;
      this.completed = false;
      this.desiredProgress = 0;
      this.currentProgress = 0;
      this.root.classList.remove('is-burn-complete', 'is-burn-reversing');
      this.root.dataset.burnState = 'editor-preview-preparing';
      this.root.dataset.burnProgress = '0.000';

      await this.prepare();
      if (this.destroyed) return;

      if (!this.initWebGL()) this.enableCssFallback('webgl-unavailable');

      this.resize();
      this.applyPresentation(0);
      this.render(this.burnProgress, 0, true);
      this.root.classList.add('is-burn-active');
      this.root.dataset.burnState = 'editor-preview-running';
      this.activeDuration = this.editorDuration;
      this.startedAt = performance.now();
      this.animationFrame = requestAnimationFrame((now) => this.tick(now));
    }

    pauseEditorPreview() {
      if (!this.isDesignMode || this.motionMode !== 'scroll' || this.destroyed) return;
      window.clearTimeout(this.editorPreviewTimer);
      cancelAnimationFrame(this.animationFrame);
      this.running = false;
      this.completed = true;
      this.desiredProgress = 1;
      this.currentProgress = 1;
      this.applyPresentation(1);
      if (this.gl && this.program) this.render(this.burnProgress, 0, true);
      this.root.classList.remove('is-burn-active', 'is-burn-reversing');
      this.root.classList.add('is-burn-complete');
      this.root.dataset.burnState = 'editor-preview-paused';
      this.root.dataset.burnDirection = 'hold';
      this.root.dataset.burnProgress = '1.000';
      this.heroVideo?.pause();
    }

    prepare() {
      if (this.preparing) return this.preparing;

      this.root.dataset.burnState = 'preparing';
      this.preparing = Promise.all([this.prepareMask(), this.prepareHeroVideo()])
        .then(() => {
          if (this.destroyed) return;
          if (this.motionMode === 'scroll') {
            if (this.initWebGL()) {
              this.resize();
              this.drawScrollProgress(this.desiredProgress);
            } else {
              this.enableCssFallback('webgl-unavailable');
              this.applyPresentation(this.desiredProgress);
            }
            this.root.dataset.burnState = this.desiredProgress >= 0.995
              ? 'complete'
              : this.desiredProgress <= 0.005
                ? 'closed'
                : 'opening';
          } else if (!this.running && !this.completed) {
            this.root.dataset.burnState = 'ready';
          }
        });
      return this.preparing;
    }

    async prepareMask() {
      if (!this.maskAtlasUrl || this.forceProcedural) {
        this.root.dataset.burnMaskState = this.forceProcedural ? 'procedural-forced' : 'atlas-missing';
        return;
      }

      this.maskImage = new Image();
      this.maskImage.decoding = 'async';
      this.maskImage.crossOrigin = 'anonymous';
      this.maskImage.src = this.maskAtlasUrl;

      if (this.maskImage.complete && this.maskImage.naturalWidth > 0) {
        this.maskReady = true;
      } else {
        this.maskReady = await waitForEvent(this.maskImage, 'load', 2400);
      }

      if (!this.maskReady || this.maskImage.naturalWidth <= 0) {
        this.maskReady = false;
        this.root.dataset.burnMaskState = 'atlas-load-failed';
        return;
      }

      this.root.dataset.burnMaskState = 'atlas-ready';
      this.uploadMaskAtlas();
    }

    async prepareHeroVideo() {
      if (!this.heroVideo) return;
      const heroExperience = this.root._moztechHeroExperience;
      if (heroExperience) {
        const isReady = await heroExperience.prepareVideo();
        if (isReady) return;
      }
      this.heroVideo.preload = 'auto';
      if (this.heroVideo.readyState < 3) this.heroVideo.load();
      if (this.heroVideo.readyState >= 3) return;
      await waitForEvent(this.heroVideo, 'canplay', 3600);
    }

    async playHeroVideo() {
      if (!this.heroVideo || !this.heroPlaybackRequested || this.destroyed) return false;
      if (!this.heroVideo.paused && !this.heroVideo.ended) return true;
      if (this.heroPlayPromise) return this.heroPlayPromise;

      const heroExperience = this.root._moztechHeroExperience;
      const startPlayback = heroExperience?.playVideo
        ? () => heroExperience.playVideo()
        : async () => {
            try {
              await this.heroVideo.play();
              return true;
            } catch (_error) {
              // Shopify's poster remains visible when autoplay is blocked.
              return false;
            }
          };

      const playPromise = Promise.resolve(startPlayback())
        .then((didStart) => {
          if (!this.heroPlaybackRequested || this.destroyed) {
            this.heroVideo.pause();
            if (!this.destroyed) this.root.dataset.mediaState = 'paused-before-title';
            return false;
          }
          return didStart !== false;
        })
        .finally(() => {
          if (this.heroPlayPromise === playPromise) this.heroPlayPromise = null;
        });
      this.heroPlayPromise = playPromise;
      return playPromise;
    }

    async start({ replay = false } = {}) {
      if (this.motionMode !== 'once' || this.destroyed || this.running || this.reducedMotion) return;
      if (this.completed && !replay) return;

      this.running = true;
      this.completed = false;
      this.root.classList.remove('is-burn-complete');
      this.root.dataset.burnState = 'waiting-for-media';

      await this.prepare();
      if (this.destroyed) return;

      if (!this.initWebGL()) this.enableCssFallback('webgl-unavailable');

      this.resize();
      this.applyPresentation(0);
      this.render(this.burnProgress, 0, true);
      this.root.classList.add('is-burn-active');
      this.root.dataset.burnState = 'running';
      this.activeDuration = this.duration;
      this.startedAt = performance.now();
      this.animationFrame = requestAnimationFrame((now) => this.tick(now));
    }

    replay() {
      if (this.destroyed || this.reducedMotion) return;
      if (this.isDesignMode && this.motionMode === 'scroll') {
        this.previewEditorBurn();
        return;
      }
      if (this.motionMode === 'scroll') {
        this.queueScrollUpdate(true);
        return;
      }
      cancelAnimationFrame(this.animationFrame);
      this.running = false;
      this.completed = false;
      this.root.classList.remove('is-burn-active', 'is-burn-complete');
      this.start({ replay: true });
    }

    tick(now) {
      if (!this.running || this.destroyed) return;
      if (this.isDesignMode && this.motionMode === 'scroll') {
        this.tickEditorPreview(now);
        return;
      }
      const elapsed = now - this.startedAt;
      const progress = clamp(elapsed / this.activeDuration, 0, 1);
      this.applyPresentation(progress);
      this.render(this.burnProgress, elapsed * 0.001, true);
      this.syncHeroVideoPlayback(progress);
      this.desiredProgress = progress;
      this.currentProgress = progress;
      this.root.dataset.burnProgress = progress.toFixed(3);

      if (progress >= 1) {
        this.completeOnce();
        return;
      }

      this.animationFrame = requestAnimationFrame((nextNow) => this.tick(nextNow));
    }

    tickEditorPreview(now) {
      if (!this.running || this.destroyed) return;

      const elapsed = Math.max(0, now - this.startedAt);
      const progress = smoothstep(0, this.editorDuration, elapsed);
      const complete = progress >= 0.999;

      this.applyPresentation(progress);
      this.render(this.burnProgress, elapsed * 0.001, true);
      this.desiredProgress = progress;
      this.currentProgress = progress;
      this.root.classList.remove('is-burn-reversing');
      this.root.classList.toggle('is-burn-complete', complete);
      this.root.classList.toggle('is-burn-active', !complete);
      this.root.dataset.burnState = complete ? 'editor-preview-product-hold' : 'editor-preview-forward';
      this.root.dataset.burnDirection = complete ? 'hold' : 'forward';
      this.root.dataset.burnProgress = progress.toFixed(3);

      this.syncHeroVideoPlayback(progress);

      if (complete) {
        this.running = false;
        this.completed = true;
        this.heroPlaybackRequested = false;
        this.heroVideo?.pause();
        this.root.dataset.mediaState = 'paused-editor-hold';
        return;
      }

      this.animationFrame = requestAnimationFrame((nextNow) => this.tick(nextNow));
    }

    queueScrollUpdate(forceResize = false) {
      if (this.destroyed || this.motionMode !== 'scroll') return;
      if (forceResize) this.needsResize = true;
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateScrollProgress();
      });
    }

    updateScrollProgress() {
      if (this.isDesignMode) {
        this.showEditorPreview();
        return;
      }
      const rect = this.root.getBoundingClientRect();
      const viewportHeight = Math.max(window.innerHeight, 1);
      // Use the complete sticky runway. The previous viewport-line mapping
      // consumed the whole reveal in less than one viewport and made the
      // fragments, burn, title and media feel like one abrupt event.
      const scrollRange = Math.max(rect.height - viewportHeight, viewportHeight * 0.65);
      const progress = clamp(-rect.top / scrollRange, 0, 1);
      const exitHandoff = Math.min(viewportHeight * 0.12, 120);
      const nearViewport = rect.bottom > exitHandoff && rect.top < viewportHeight * 1.35;

      if (!nearViewport) {
        this.heroPlaybackRequested = false;
        if (this.heroVideo && !this.heroVideo.paused) {
          this.heroVideo.pause();
          this.root.dataset.mediaState = 'paused-offscreen';
        }
        return;
      }

      const progressChanged = this.currentProgress < 0 || Math.abs(progress - this.currentProgress) > 0.0001;
      if (!progressChanged && (!this.needsResize || !nearViewport)) return;
      const previousProgress = this.currentProgress < 0 ? progress : this.currentProgress;
      const reversing = progress < previousProgress - 0.001;

      this.desiredProgress = progress;
      this.currentProgress = progress;
      this.applyPresentation(progress);
      const isReversing = reversing && progress < 0.999;
      const isComplete = progress >= 0.995;
      this.root.classList.toggle('is-burn-reversing', isReversing);
      this.root.classList.toggle('is-burn-complete', isComplete);
      this.root.classList.toggle('is-burn-active', !isComplete);
      const burnState = isComplete
        ? 'complete'
        : progress <= 0.005
          ? 'closed'
          : reversing
            ? 'reversing'
            : 'opening';
      const burnDirection = reversing ? 'reverse' : 'forward';
      const burnProgress = progress.toFixed(2);
      if (this.root.dataset.burnState !== burnState) this.root.dataset.burnState = burnState;
      if (this.root.dataset.burnDirection !== burnDirection) this.root.dataset.burnDirection = burnDirection;
      if (this.root.dataset.burnProgress !== burnProgress) this.root.dataset.burnProgress = burnProgress;

      // Decode early, but begin motion only after the title reveal starts.
      // Reverse scrolling pauses on the current frame so the same media can
      // resume without tying the burn timeline to video duration.
      this.syncHeroVideoPlayback(progress);

      if (this.needsResize) {
        this.needsResize = false;
        this.resize();
      }

      if (this.gl && this.program) this.drawScrollProgress(this.desiredProgress);
      else if (!this.preparing) this.prepare();

      this.lastScrollY = window.scrollY;
    }

    drawScrollProgress(progress) {
      if (this.destroyed || this.motionMode !== 'scroll') return;
      if (!this.initWebGL()) {
        this.enableCssFallback('webgl-unavailable');
        this.applyPresentation(progress);
        return;
      }
      if (this.needsResize || !this.canvas.width || !this.canvas.height) this.resize();
      if (Math.abs(progress - this.presentationProgress) > 0.0001) this.applyPresentation(progress);
      this.render(this.burnProgress, this.burnProgress * this.maskDuration, true);
    }

    applyPresentation(progress) {
      const normalized = clamp(progress, 0, 1);
      this.presentationProgress = normalized;
      // When the editable manifesto section sits immediately above this Hero,
      // it has already performed the idea/sketch convergence. Repeating a
      // second fragment-gather scene here made the story feel long and
      // disconnected. Enter the center burn almost immediately instead.
      const phaseMap = this.hasStoryHandoff
        ? { gatherStart: 0, gatherEnd: 0.015, burnStart: 0.035, burnEnd: 0.47, contentStart: 0.50, contentEnd: 0.70, dockStart: 0.68, dockEnd: 0.86 }
        : this.pacing === 'compact'
          ? { gatherStart: 0.01, gatherEnd: 0.25, burnStart: 0.29, burnEnd: 0.63, contentStart: 0.65, contentEnd: 0.82, dockStart: 0.80, dockEnd: 0.96 }
          : this.pacing === 'cinematic'
            ? { gatherStart: 0.02, gatherEnd: 0.36, burnStart: 0.41, burnEnd: 0.75, contentStart: 0.77, contentEnd: 0.91, dockStart: 0.87, dockEnd: 0.985 }
            : { gatherStart: 0.02, gatherEnd: 0.32, burnStart: 0.37, burnEnd: 0.69, contentStart: 0.71, contentEnd: 0.87, dockStart: 0.84, dockEnd: 0.98 };
      const gatherProgress = smoothstep(phaseMap.gatherStart, phaseMap.gatherEnd, normalized);
      const burnProgress = smoothstep(phaseMap.burnStart, phaseMap.burnEnd, normalized);
      const contentProgress = smoothstep(phaseMap.contentStart, phaseMap.contentEnd, normalized);
      const dockProgress = smoothstep(phaseMap.dockStart, phaseMap.dockEnd, normalized);
      const burnSpan = phaseMap.burnEnd - phaseMap.burnStart;
      // The poster/video surface must already exist behind the black curtain.
      // Spatial reveal is handled by the Luma canvas; delaying the entire media
      // opacity left the newly burned center black on Safari and mobile GPUs.
      const mediaStart = phaseMap.burnStart + burnSpan * 0.08;
      const mediaReady = phaseMap.burnStart + burnSpan * 0.36;
      const mediaProgress = smoothstep(mediaStart, mediaReady, normalized);
      const fallbackRadiusProgress = 1 - Math.pow(1 - burnProgress, 1.55);
      const ignitionOpacity = smoothstep(0.012, 0.08, burnProgress)
        * (1 - smoothstep(0.24, 0.47, burnProgress));
      const thermalOpacity = Math.sin(Math.PI * contentProgress) * 0.92;

      this.videoGateProgress = phaseMap.contentStart - 0.015;

      this.burnProgress = burnProgress;
      this.root.style.setProperty('--moz-burn-media-opacity', (0.02 + mediaProgress * 0.94).toFixed(4));
      this.root.style.setProperty('--moz-burn-media-scale', (1.025 - mediaProgress * 0.025).toFixed(4));
      this.root.style.setProperty('--moz-burn-media-blur', `${((1 - mediaProgress) * 5).toFixed(2)}px`);
      this.root.style.setProperty('--moz-burn-media-saturation', (0.72 + mediaProgress * 0.28).toFixed(4));
      this.root.style.setProperty('--moz-burn-content-opacity', contentProgress.toFixed(4));
      this.root.style.setProperty('--moz-burn-content-y', `${((1 - contentProgress) * 22).toFixed(2)}px`);
      this.root.style.setProperty('--moz-burn-dock-opacity', dockProgress.toFixed(4));
      this.root.style.setProperty('--moz-burn-dock-y', `${((1 - dockProgress) * 20).toFixed(2)}px`);
      if (this.root.classList.contains('is-burn-css-fallback')) {
        this.root.style.setProperty('--moz-burn-fallback-radius', `${(fallbackRadiusProgress * 120).toFixed(2)}%`);
        this.root.style.setProperty('--moz-burn-ignition-opacity', ignitionOpacity.toFixed(4));
      }
      this.root.style.setProperty('--moz-title-sweep-right', `${((1 - contentProgress) * 100).toFixed(2)}%`);
      this.root.style.setProperty('--moz-title-thermal-opacity', thermalOpacity.toFixed(4));
      const maskProgressValue = burnProgress.toFixed(2);
      const gatherProgressValue = gatherProgress.toFixed(2);
      const mediaProgressValue = mediaProgress.toFixed(2);
      const burnPhase = normalized < phaseMap.burnStart
        ? 'gathering'
        : normalized < phaseMap.contentStart
          ? 'burning'
          : normalized < phaseMap.dockStart
            ? 'title'
            : 'product';
      if (this.root.dataset.burnMaskProgress !== maskProgressValue) this.root.dataset.burnMaskProgress = maskProgressValue;
      if (this.root.dataset.burnGatherProgress !== gatherProgressValue) this.root.dataset.burnGatherProgress = gatherProgressValue;
      if (this.root.dataset.burnMediaProgress !== mediaProgressValue) this.root.dataset.burnMediaProgress = mediaProgressValue;
      if (this.root.dataset.burnPhase !== burnPhase) this.root.dataset.burnPhase = burnPhase;

      if (!this.fragments.length) return burnProgress;
      if (this.hasStoryHandoff) {
        if (!this.handoffFragmentsHidden) {
          this.fragments.forEach((fragment) => {
            fragment.style.opacity = '0';
            fragment.style.visibility = 'hidden';
          });
          this.handoffFragmentsHidden = true;
        }
        return burnProgress;
      }
      const layouts = [
        [-0.43, -0.34, -24], [0.39, -0.31, 31], [-0.35, -0.03, 18], [0.44, 0.02, -17],
        [-0.29, 0.32, 28], [0.31, 0.35, -32], [-0.08, -0.42, 14], [0.09, 0.43, -12]
      ];
      const customLayouts = [
        [-0.36, -0.29, -12], [0.34, -0.25, 11], [-0.39, 0.17, 9], [0.37, 0.2, -10],
        [-0.18, -0.4, 16], [0.17, 0.4, -15], [-0.46, 0.01, -6], [0.45, -0.01, 7]
      ];
      const clusterLayouts = [
        [-14, -10, -4], [12, -12, 3], [-13, 11, 4], [14, 10, -3],
        [-4, -16, 2], [5, 16, -2], [-18, 1, -3], [18, -1, 3]
      ];
      const initialSpread = this.hasStoryHandoff ? 0.22 : 1;
      const travel = initialSpread * (1 - gatherProgress);
      const fade = 1 - smoothstep(phaseMap.burnStart + 0.015, phaseMap.burnEnd - 0.035, normalized);
      const width = Math.min(window.innerWidth, 1440);
      const height = Math.min(window.innerHeight, 980);
      const isMobile = window.innerWidth <= 768;

      let customIndex = 0;
      this.fragments.forEach((fragment, index) => {
        const isCustom = fragment.classList.contains('is-custom');
        const isLegacy = fragment.classList.contains('is-legacy');
        if (isMobile && this.hasCustomFragments && isLegacy) {
          fragment.style.opacity = '0';
          return;
        }

        const layout = isCustom
          ? customLayouts[customIndex++ % customLayouts.length]
          : layouts[index % layouts.length];
        const [fallbackX, fallbackY, fallbackRotation] = layout;
        const [clusterX, clusterY, clusterRotation] = clusterLayouts[index % clusterLayouts.length];
        const autoPosition = isCustom && fragment.dataset.fragmentAuto !== 'false';
        const configuredX = Number(fragment.dataset[isMobile ? 'fragmentMobileX' : 'fragmentX']);
        const configuredY = Number(fragment.dataset[isMobile ? 'fragmentMobileY' : 'fragmentY']);
        const configuredRotation = Number(fragment.dataset[isMobile ? 'fragmentMobileRotation' : 'fragmentRotation']);
        const configuredOpacity = Number(fragment.dataset.fragmentOpacity);
        const x = !autoPosition && Number.isFinite(configuredX) ? configuredX / 100 - 0.5 : fallbackX;
        const y = !autoPosition && Number.isFinite(configuredY) ? configuredY / 100 - 0.5 : fallbackY;
        const rotation = !autoPosition && Number.isFinite(configuredRotation) ? configuredRotation : fallbackRotation;
        const blockOpacity = Number.isFinite(configuredOpacity) ? clamp(configuredOpacity, 0.2, 1) : 1;
        const stagger = 1 - index * 0.025;
        const opacity = clamp(fade * this.fragmentIntensity * blockOpacity * stagger, 0, 1);
        const scale = (0.26 + travel * (0.56 + (index % 3) * 0.06)) * (isCustom ? this.fragmentSizeScale : 1);
        const translateX = x * width * travel + clusterX * gatherProgress;
        const translateY = y * height * travel + clusterY * gatherProgress;
        const activeRotation = rotation * travel + clusterRotation * gatherProgress;
        fragment.style.opacity = opacity.toFixed(3);
        fragment.style.transform = `translate3d(${translateX.toFixed(1)}px, ${translateY.toFixed(1)}px, 0) translate(-50%, -50%) rotate(${activeRotation.toFixed(1)}deg) scale(${scale.toFixed(3)})`;
      });

      return burnProgress;
    }

    syncHeroVideoPlayback(progress) {
      if (!this.heroVideo) return;
      const gate = Number.isFinite(this.videoGateProgress) ? this.videoGateProgress : 0.70;
      const startGate = Math.min(1, gate + 0.02);
      const stopGate = Math.max(0, gate - 0.035);

      if (!this.heroPlaybackRequested && progress >= startGate) {
        this.heroPlaybackRequested = true;
        this.playHeroVideo();
        return;
      }

      if (this.heroPlaybackRequested && progress < stopGate) {
        this.heroPlaybackRequested = false;
        if (!this.heroVideo.paused) this.heroVideo.pause();
        if (this.root.dataset.mediaState !== 'paused-before-title') {
          this.root.dataset.mediaState = 'paused-before-title';
        }
        return;
      }

      if (this.heroPlaybackRequested && this.heroVideo.paused) {
        this.playHeroVideo();
      } else if (!this.heroPlaybackRequested && this.root.dataset.mediaState !== 'paused-before-title') {
        this.root.dataset.mediaState = 'paused-before-title';
      }
    }

    createShader(type, source) {
      const shader = this.gl.createShader(type);
      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);
      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        console.warn('[MOZTECH burn reveal] Shader compilation failed.', this.gl.getShaderInfoLog(shader));
        this.gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    enableCssFallback(reason) {
      this.root.classList.add('is-burn-css-fallback');
      this.root.dataset.burnRenderer = 'css';
      this.root.dataset.burnFallbackReason = reason;
      this.needsResize = false;
    }

    initWebGL() {
      if (this.gl && this.program) return true;
      if (this.forceCssFallback) {
        this.enableCssFallback('debug-forced');
        return false;
      }

      const gl = this.canvas.getContext('webgl', {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false,
        powerPreference: 'low-power'
      });
      if (!gl) {
        this.enableCssFallback('webgl-context');
        return false;
      }

      this.gl = gl;
      const vertexShader = this.createShader(gl.VERTEX_SHADER, VERTEX_SHADER);
      const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      if (!vertexShader || !fragmentShader) {
        this.enableCssFallback('shader-compile');
        return false;
      }

      const program = gl.createProgram();
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.warn('[MOZTECH burn reveal] Program link failed.', gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        this.enableCssFallback('shader-link');
        return false;
      }

      this.program = program;
      this.positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

      const position = gl.getAttribLocation(program, 'aPosition');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      this.maskTexture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.maskTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        1,
        1,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 255])
      );

      gl.useProgram(program);
      gl.uniform1i(gl.getUniformLocation(program, 'uMask'), 0);
      this.uniforms = {
        resolution: gl.getUniformLocation(program, 'uResolution'),
        maskTexel: gl.getUniformLocation(program, 'uMaskTexel'),
        maskGrid: gl.getUniformLocation(program, 'uMaskGrid'),
        maskTextureSize: gl.getUniformLocation(program, 'uMaskTextureSize'),
        glowColor: gl.getUniformLocation(program, 'uGlowColor'),
        maskReady: gl.getUniformLocation(program, 'uMaskReady'),
        maskFrameA: gl.getUniformLocation(program, 'uMaskFrameA'),
        maskFrameB: gl.getUniformLocation(program, 'uMaskFrameB'),
        maskFrameMix: gl.getUniformLocation(program, 'uMaskFrameMix'),
        progress: gl.getUniformLocation(program, 'uProgress'),
        time: gl.getUniformLocation(program, 'uTime')
      };
      gl.uniform2f(this.uniforms.maskTexel, 1 / this.maskFrameWidth, 1 / this.maskFrameHeight);
      gl.uniform2f(this.uniforms.maskGrid, this.maskAtlasColumns, this.maskAtlasRows);
      gl.uniform2f(this.uniforms.maskTextureSize, 1, 1);
      gl.uniform3f(this.uniforms.glowColor, ...this.glowColor);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);

      this.maskUploaded = false;
      this.uploadMaskAtlas();
      this.root.classList.remove('is-burn-css-fallback');
      this.root.dataset.burnRenderer = this.maskUploaded ? 'webgl-luma-atlas' : 'webgl-procedural';
      delete this.root.dataset.burnFallbackReason;

      return true;
    }

    uploadMaskAtlas() {
      if (!this.gl || !this.program || !this.maskTexture || !this.maskReady || !this.maskImage || this.maskUploaded) return;

      const gl = this.gl;
      try {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.maskTexture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.maskImage);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.useProgram(this.program);
        gl.uniform2f(this.uniforms.maskTextureSize, this.maskImage.naturalWidth, this.maskImage.naturalHeight);
        this.maskUploaded = true;
        this.root.dataset.burnRenderer = 'webgl-luma-atlas';
        this.root.dataset.burnMaskState = 'atlas-uploaded';
      } catch (_error) {
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        this.maskReady = false;
        this.maskUploaded = false;
        this.root.dataset.burnRenderer = 'webgl-procedural';
        this.root.dataset.burnMaskState = 'atlas-upload-failed';
      }
    }

    resize() {
      if (!this.gl) return;
      // Size the framebuffer from the visible sticky canvas, not the full
      // 195–250dvh scroll runway. The runway is intentionally taller than the
      // viewport and previously distorted portrait rendering into a narrow
      // buffer, pushing the burn contour off-screen on phones.
      const rect = this.canvas.getBoundingClientRect();
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      const ratio = Math.min(window.devicePixelRatio || 1, mobile ? 1 : 1.25);
      const scale = mobile ? 0.78 : 0.74;
      const width = Math.max(2, Math.min(1280, Math.round(rect.width * ratio * scale)));
      const height = Math.max(2, Math.min(720, Math.round(rect.height * ratio * scale)));

      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }

      this.gl.viewport(0, 0, width, height);
      this.gl.useProgram(this.program);
      this.gl.uniform2f(this.uniforms.resolution, width, height);
      this.needsResize = false;
    }

    render(progress, time, allowMask = true) {
      const gl = this.gl;
      if (!gl || !this.program) return;

      gl.useProgram(this.program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.maskTexture);

      this.uploadMaskAtlas();
      const useMask = allowMask && this.maskReady && this.maskUploaded;
      const maskProgress = Math.pow(clamp(progress, 0, 1), 1.05);
      const frameFloat = maskProgress * Math.max(this.maskFrameCount - 1, 0);
      const frameA = Math.floor(frameFloat);
      const frameB = Math.min(frameA + 1, this.maskFrameCount - 1);
      gl.uniform1f(this.uniforms.maskReady, useMask ? 1 : 0);
      gl.uniform1f(this.uniforms.maskFrameA, frameA);
      gl.uniform1f(this.uniforms.maskFrameB, frameB);
      gl.uniform1f(this.uniforms.maskFrameMix, frameFloat - frameA);
      gl.uniform1f(this.uniforms.progress, progress);
      gl.uniform1f(this.uniforms.time, time);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    completeOnce() {
      cancelAnimationFrame(this.animationFrame);
      this.running = false;
      this.completed = true;
      this.root.classList.remove('is-burn-active');
      this.root.classList.add('is-burn-complete');
      this.root.dataset.burnState = this.reducedMotion ? 'reduced-motion' : 'complete';

    }

    disposeWebGL() {
      if (!this.gl) return;
      if (this.maskTexture) this.gl.deleteTexture(this.maskTexture);
      if (this.positionBuffer) this.gl.deleteBuffer(this.positionBuffer);
      if (this.program) this.gl.deleteProgram(this.program);
      this.maskTexture = null;
      this.positionBuffer = null;
      this.program = null;
      this.maskUploaded = false;
      const loseContext = this.gl.getExtension('WEBGL_lose_context');
      loseContext?.loseContext();
      this.gl = null;
    }

    destroy() {
      this.destroyed = true;
      this.heroPlaybackRequested = false;
      this.heroPlayPromise = null;
      cancelAnimationFrame(this.animationFrame);
      cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.editorPreviewTimer);
      this.prepareObserver?.disconnect();
      this.startObserver?.disconnect();
      this.resizeObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.maskImage) this.maskImage.src = '';
      this.heroVideo?.pause();
      this.canvas?.removeEventListener('webglcontextlost', this.onContextLost, false);
      this.canvas?.removeEventListener('webglcontextrestored', this.onContextRestored, false);
      this.disposeWebGL();
      this.root.classList.remove('is-burn-active', 'is-burn-css-fallback');
      delete this.root._moztechBurnReveal;
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-moztech-hero][data-burn-enabled="true"]')) roots.push(scope);
    scope.querySelectorAll?.('[data-moztech-hero][data-burn-enabled="true"]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root._moztechBurnReveal) return;
      root._moztechBurnReveal = new MoztechBurnRevealInstance(root);
    });
  };

  const destroy = (scope) => {
    const root = scope.matches?.('[data-moztech-hero]') ? scope : scope.querySelector?.('[data-moztech-hero]');
    root?._moztechBurnReveal?.destroy();
  };

  window.MoztechBurnReveal = { version: VERSION, init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches?.('[data-moztech-hero]')
      ? event.target
      : event.target.closest?.('[data-moztech-hero]') || event.target.querySelector?.('[data-moztech-hero]');
    root?._moztechBurnReveal?.replay();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-moztech-hero]') || event.target.querySelector?.('[data-moztech-hero]');
    root?._moztechBurnReveal?.replay();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const root = event.target.matches?.('[data-moztech-hero]')
      ? event.target
      : event.target.closest?.('[data-moztech-hero]') || event.target.querySelector?.('[data-moztech-hero]');
    root?._moztechBurnReveal?.pauseEditorPreview();
  });
})();
